/**
 * Vue 2 wrapper helper
 */
export default function() {
    /**
     *
     * @param { Object } context
     * @param { * } context.cellContent Content to be rendered in cell (set by renderer)
     * @param { Column } context.column Column being rendered
     * @returns { Boolean } `true` if there is a Vue Renderer in this cell, `false` otherwise
     */
    function hasFrameworkRenderer({ cellContent, column }) {
        return (
            cellContent &&
            typeof cellContent === 'object' &&
            column.data.vue
        );
    }

    /**
     * Hook called by instance when rendering cells within
     * Row::renderCell(), creates portals for JSX supplied by renderers
     * @param {Object} context
     * @param {Object} context.rendererData Data passed from renderCell
     * @param {Object} context.cellElementData Data passed from renderCell
     */
    function processCellContent({
        rendererData,
        rendererData : { record },
        rendererData : { cellElement },
        rendererHtml : cellContent
    }) {
        if (!rendererData.column.data.vue || record.isSpecialRow || typeof cellContent !== 'object') {
            return true;
        }

        const // Bound array of cell components + refs from the Grid Vue component
            me                       = this,
            { cellInstances, $refs } = me,
            // Vue component currently displayed in the cell
            { vueInstance }          = cellElement,
            // Key, used as both key and ref
            key                      = `${rendererData.column.id}-${record.id}`,
            // Reuse already existing component for this cell (refs in v-for are arrays)
            [existing]               = $refs[key] || [];

        // Cell has a vue instance associated with it, move its element back to the cell instance container before
        // adding a new instance. It will be pulled in again if another cell renders the same record later
        if (vueInstance) {
            $refs.cellInstancesHolder.appendChild(vueInstance.$el);

            // If we're removing vue element from the cell, we need to remove references to it and from it, so
            // current cell doesn't know about vueInstance and vueInstance doesn't know about cell it was rendered to
            if (vueInstance.cellElement) {
                delete vueInstance.cellElement.vueInstance;
            }
            delete vueInstance.cellElement;
        }

        // There is an existing instance previously rendered, pull its element in from the cell instance container
        if (existing) {
            // `existing` is a VueComponent.
            // `existing.$props.record` is the record which holds the data to render into the component.
            // Note, if store.syncDataOnLoad is falsy and you load the data with the same ID,
            // the record and existing.$props.record will be different objects, and existing.$props.record
            // will hold the old value. Make sure `syncDataOnLoad` is true.

            // Move its element to the cell
            cellElement.appendChild(existing.$el);

            // Remember that cell is displaying it
            cellElement.vueInstance = existing;

            // When reassigning cell remove reference to the vueInstance from the previous cellElement
            if (existing.cellElement) {
                delete existing.cellElement.vueInstance;
            }

            existing.cellElement = cellElement;
        }
        // No existing instance, create a new one and pull its element in on next Vue tick
        else {
            // Data from the renderer that will be turned into a vue instance by the v-for in App.vue
            cellInstances.push({ ...cellContent, key });

            // Wait until the vue component is mounted
            me.$nextTick(() => {
                // Get the newly rendered Vue component (refs in v-for are arrays)
                const [instance] = $refs[key];

                // Move its element to the cell
                cellElement.appendChild(instance.$el);
                // Remember that cell is displaying it
                cellElement.vueInstance = instance;

                // Let vue instance to know about the cell it is rendered to, so when it gets moved to cache it can
                // clear itself from the cellElement
                instance.cellElement = cellElement;
            });
        }
        return false;
    }

    return {
        processCellContent,
        hasFrameworkRenderer
    };
}
